import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { depositApi, depositQueryKeys, type DepositTxn } from "../../../shared/api/deposit";
import { apiError } from "../../../shared/api/client";
import { formatVndWithUnit, formatDateTime } from "../../../shared/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

const TXN_LABEL: Record<DepositTxn["kind"], string> = {
  DEPOSIT: "Nộp tạm ứng",
  APPLY: "Trừ vào hóa đơn",
  REFUND: "Hoàn tạm ứng",
  ADJUST: "Điều chỉnh",
};

/** Tạm ứng — deposit balance, take/refund, and history for one patient. */
export function PatientDepositSection({ patientId }: { patientId: string }) {
  const qc = useQueryClient();
  const balance = useQuery({
    queryKey: depositQueryKeys.balance(patientId),
    queryFn: () => depositApi.balance(patientId),
  });
  const txns = useQuery({
    queryKey: depositQueryKeys.txns(patientId),
    queryFn: () => depositApi.history(patientId),
  });

  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: depositQueryKeys.balance(patientId) });
    qc.invalidateQueries({ queryKey: depositQueryKeys.txns(patientId) });
    setAmount("");
    setReason("");
    setError(null);
  };

  const take = useMutation({
    mutationFn: () => depositApi.deposit(patientId, Number(amount), reason || null),
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e).detail),
  });
  const refund = useMutation({
    mutationFn: () => depositApi.refund(patientId, Number(amount), reason || null),
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e).detail),
  });

  const amt = Number(amount);
  const canSubmit = Number.isFinite(amt) && amt > 0;

  return (
    <div className="grid gap-4">
      <Card>
        <CardContent className="grid gap-3 p-4">
          <div className="flex items-baseline gap-2">
            <span className="text-sm text-muted-foreground">Số dư tạm ứng:</span>
            <span className="text-xl font-semibold text-foreground">
              {formatVndWithUnit(balance.data ?? 0)}
            </span>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground" htmlFor="dep-amount">
                Số tiền
              </label>
              <Input
                id="dep-amount"
                type="number"
                min={0}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="min-w-[160px] flex-1 space-y-1.5">
              <label className="text-sm text-muted-foreground" htmlFor="dep-reason">
                Ghi chú
              </label>
              <Input
                id="dep-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="VD: đặt cọc liệu trình…"
              />
            </div>
            <Button disabled={!canSubmit || take.isPending} onClick={() => take.mutate()}>
              {take.isPending ? "Đang lưu…" : "Nộp tạm ứng"}
            </Button>
            <Button
              variant="outline"
              disabled={!canSubmit || refund.isPending}
              onClick={() => refund.mutate()}
            >
              {refund.isPending ? "Đang hoàn…" : "Hoàn tạm ứng"}
            </Button>
          </div>
          <p className="m-0 text-xs text-muted-foreground">
            Trừ tạm ứng vào hóa đơn thực hiện ở màn hình chi tiết hóa đơn.
          </p>
          {error && <p className="m-0 text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>

      {(txns.data ?? []).length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-muted-foreground">Lịch sử tạm ứng</h3>
            <ul className="grid gap-2">
              {(txns.data ?? []).map((t, i) => (
                <li key={i} className="flex items-center justify-between border-b pb-2 last:border-b-0 text-sm">
                  <span>
                    <span className="font-medium">{TXN_LABEL[t.kind]}</span>
                    {t.reason && <span className="text-muted-foreground"> · {t.reason}</span>}
                    <span className="block text-xs text-muted-foreground">{formatDateTime(t.at)}</span>
                  </span>
                  <span className={t.amount < 0 ? "text-destructive" : "text-emerald-600"}>
                    {t.amount < 0 ? "" : "+"}
                    {formatVndWithUnit(t.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
